import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineGrid,
  InlineStack,
  Page,
  Select,
  Spinner,
  Text,
} from '@shopify/polaris'
import { RefreshIcon } from '@shopify/polaris-icons'
import { SectionSidebar }    from '../../../components/layout/SectionSidebar'
import { getShop }           from '../../../sdk/shop'
import { engineApi }         from '../../../sdk/engine.api'
import { execApi }           from '../../../sdk/exec.api'
import { operationsApi }     from '../../../sdk/operations.api'
import { inventoryApi }      from '../../../sdk/inventory.api'
import { useEnabledModules } from '../../../hooks/useEnabledModules'
import { isFeatureEnabled }  from '../../../config/features'
import { useOpsPermissions } from '../../../hooks/useOpsPermissions'
import { hasAnyOrdsPerm, hasAnyOpsPerm, hasAnyInvPerm } from '../../../types/ops-permissions'
import type { WorkflowMetrics }    from '../../../sdk/engine.api'
import type { ExecDashboard }      from '../../../sdk/exec.api'
import type { PickupStatusResponse, OverviewStatsResponse, OrderStatsResponse } from '../../../sdk/operations.api'

type SidebarItem = { label: string; href: string }

const ALL_SIDEBAR_ITEMS: (SidebarItem & { permCheck: (p: ReturnType<typeof useOpsPermissions>) => boolean })[] = [
  { label: 'Orders',     href: '/workspace/operations/orders',     permCheck: p => hasAnyOrdsPerm(p) },
  { label: 'Operations', href: '/workspace/operations/operations', permCheck: p => hasAnyOpsPerm(p)  },
  { label: 'Inventory',  href: '/workspace/operations/inventory',  permCheck: p => hasAnyInvPerm(p)  },
  { label: 'Activity',   href: '/workspace/operations/activity',   permCheck: p => p.activity        },
]

const PERIODS = [
  { label: 'Today',      value: 'today'      },
  { label: 'Yesterday',  value: 'yesterday'  },
  { label: 'This Month', value: 'this_month' },
]

function toExecPeriod(p: string): 'today' | 'this_month' | null {
  if (p === 'today')      return 'today'
  if (p === 'this_month') return 'this_month'
  return null
}

type FifoSummary = { full: number; partial: number; none: number; total: number }

// ── MetricCard ────────────────────────────────────────────────────────────────

type MetricCardProps = {
  label: string
  value: string | number | null
  sub?:  string
  tone?: 'success' | 'critical' | 'caution'
}

function MetricCard({ label, value, sub, tone }: MetricCardProps) {
  const display  = value === null ? '—' : String(value)
  const textTone = tone === 'caution' ? undefined : tone
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
        <Text as="p" variant="headingLg" tone={textTone}>{display}</Text>
        {sub && <Text as="p" variant="bodySm" tone="subdued">{sub}</Text>}
      </BlockStack>
    </Card>
  )
}

function SectionTitle({ title, sub }: { title: string; sub?: string }) {
  return (
    <BlockStack gap="050">
      <Text as="h2" variant="headingMd">{title}</Text>
      {sub && <Text as="p" variant="bodySm" tone="subdued">{sub}</Text>}
    </BlockStack>
  )
}

// ── helpers ───────────────────────────────────────────────────────────────────

function n(v: number | null | undefined): string | null {
  return v == null ? null : v.toLocaleString('en-EG', { maximumFractionDigits: 0 })
}

function pct(count: number, total: number): string {
  return total === 0 ? '—' : `${Math.round(count / total * 100)}%`
}

function fmtHour(h: number): string {
  const period = h < 12 ? 'AM' : 'PM'
  const disp   = h % 12 === 0 ? 12 : h % 12
  return `${disp}:00 ${period}`
}

// ── PerformancePage ───────────────────────────────────────────────────────────

export function PerformancePage() {
  const shop     = getShop()
  const enabled  = useEnabledModules()
  const hasBosta = isFeatureEnabled('BOSTA_SHIPPING')
  const hasFifo  = isFeatureEnabled('FIFO_INVENTORY')
  const perms    = useOpsPermissions()

  const sidebarItems = useMemo<SidebarItem[]>(
    () => ALL_SIDEBAR_ITEMS.filter(item => item.permCheck(perms)),
    [perms],
  )

  const [period,       setPeriod]       = useState('today')
  const [staticReady,  setStaticReady]  = useState(false)
  const [periodLoading,setPeriodLoading]= useState(true)
  const [error,        setError]        = useState<string | null>(null)

  const [cfMetrics,     setCfMetrics]     = useState<WorkflowMetrics | null>(null)
  const [scMetrics,     setScMetrics]     = useState<WorkflowMetrics | null>(null)
  const [csMetrics,     setCsMetrics]     = useState<WorkflowMetrics | null>(null)
  const [pickupData,    setPickupData]    = useState<PickupStatusResponse | null>(null)
  const [fifoData,      setFifoData]      = useState<FifoSummary | null>(null)
  const [orderStats,    setOrderStats]    = useState<OrderStatsResponse | null>(null)
  const [overviewStats, setOverviewStats] = useState<OverviewStatsResponse | null>(null)
  const [execData,      setExecData]      = useState<ExecDashboard | null>(null)

  // Static data: queue metrics, pickup status, FIFO, order totals — load once
  const loadStatic = useCallback(async () => {
    if (!shop) return
    try {
      await Promise.all([
        operationsApi.orderStats(shop).then(setOrderStats).catch(() => {}),
        hasBosta
          ? operationsApi.getPickupStatus(shop).then(setPickupData).catch(() => {})
          : Promise.resolve(),
        enabled.confirmation
          ? engineApi.metrics(shop, 'confirmation').then(setCfMetrics).catch(() => {})
          : Promise.resolve(),
        enabled.shippingConfirmation
          ? engineApi.metrics(shop, 'shipping_confirmation').then(setScMetrics).catch(() => {})
          : Promise.resolve(),
        enabled.customerService
          ? engineApi.metrics(shop, 'customer_service').then(setCsMetrics).catch(() => {})
          : Promise.resolve(),
        hasFifo
          ? inventoryApi.orderReserved(shop).then(data => {
              let full = 0, partial = 0, none = 0
              for (const order of data.orders) {
                const statuses = order.items.map(i => i.status)
                if (statuses.every(s => s === 'full'))    full++
                else if (statuses.every(s => s === 'none')) none++
                else partial++
              }
              setFifoData({ full, partial, none, total: data.orders.length })
            }).catch(() => {})
          : Promise.resolve(),
      ])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setStaticReady(true)
    }
  }, [shop, hasBosta, hasFifo, enabled.confirmation, enabled.shippingConfirmation, enabled.customerService])

  // Period data: overview stats + exec dashboard — reload on period change
  const loadPeriod = useCallback(async (p: string) => {
    if (!shop) return
    setPeriodLoading(true)
    try {
      const execPeriod = toExecPeriod(p)
      await Promise.all([
        operationsApi.overviewStats(shop, p).then(setOverviewStats).catch(() => {}),
        execPeriod
          ? execApi.getDashboard(shop, 'ops-manager', execPeriod).then(setExecData).catch(() => setExecData(null))
          : Promise.resolve(setExecData(null)),
      ])
    } finally {
      setPeriodLoading(false)
    }
  }, [shop])

  useEffect(() => { loadStatic() }, [loadStatic])
  useEffect(() => { loadPeriod(period) }, [loadPeriod, period])

  if (!perms.overview) {
    return (
      <Page title="Operations" backAction={{ content: 'Workspace', url: '/workspace-hub' }}>
        <Card><Text as="p" tone="subdued">You don't have access to the Operations dashboard.</Text></Card>
      </Page>
    )
  }

  const s       = overviewStats
  const summary = execData?.summary ?? {}

  // CF confirmed / SC approved for the period (reversed from rate × sessions)
  const cfConfirmedPeriod = (summary['cfSessions'] != null && summary['confirmationRate'] != null)
    ? Math.round((summary['cfSessions'] as number) * (summary['confirmationRate'] as number) / 100)
    : null
  const scApprovedPeriod = (summary['scSessions'] != null && summary['scRate'] != null)
    ? Math.round((summary['scSessions'] as number) * (summary['scRate'] as number) / 100)
    : null
  const throughputRate = (cfConfirmedPeriod != null && scApprovedPeriod != null && cfConfirmedPeriod > 0)
    ? Math.round(scApprovedPeriod / cfConfirmedPeriod * 100)
    : null

  // Queue percentages — denominator = total orders for meaningful context
  const totalOrders  = orderStats?.total ?? 0
  const cancelledPct = pct(orderStats?.cancelled ?? 0, totalOrders)
  const cfQueuePct   = pct(cfMetrics?.queued ?? 0, totalOrders)
  const scQueuePct   = pct(scMetrics?.queued ?? 0, totalOrders)

  const periodLabel = PERIODS.find(p => p.value === period)?.label ?? ''
  const hasExec     = toExecPeriod(period) !== null && execData !== null

  return (
    <Page
      title="Operations"
      subtitle="Operations manager dashboard"
      backAction={{ content: 'Workspace', url: '/workspace-hub' }}
      secondaryActions={[{
        content: 'Refresh',
        icon: RefreshIcon,
        onAction: () => { loadStatic(); loadPeriod(period) },
      }]}
    >
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        <SectionSidebar items={sidebarItems} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <BlockStack gap="600">

            {!staticReady && (
              <InlineStack gap="200" blockAlign="center">
                <Spinner size="small" />
                <Text as="p" variant="bodySm" tone="subdued">Loading...</Text>
              </InlineStack>
            )}

            {error && (
              <Banner tone="critical">
                <InlineStack gap="200" blockAlign="center">
                  <Text as="p">{error}</Text>
                  <Button onClick={() => { loadStatic(); loadPeriod(period) }} size="slim">Retry</Button>
                </InlineStack>
              </Banner>
            )}

            {/* ── Section 1: Alerts ─────────────────────────────────────── */}
            <BlockStack gap="300">
              <SectionTitle title="Alerts" sub="Items that need immediate attention" />
              <InlineGrid columns={{ xs: 2, sm: 3 }} gap="300">
                {hasBosta && (
                  <MetricCard
                    label="Pickup Warnings"
                    value={n(pickupData?.warnings)}
                    sub="Handed to Bosta — no confirmation yet"
                    tone={(pickupData?.warnings ?? 0) > 0 ? 'critical' : 'success'}
                  />
                )}
                {enabled.shippingConfirmation && (
                  <MetricCard
                    label={`Stale in SC (${s?.stale.thresholdDays ?? 3}+ days)`}
                    value={n(s?.stale.count)}
                    sub="Confirmed orders not shipped yet"
                    tone={(s?.stale.count ?? 0) > 0 ? 'caution' : 'success'}
                  />
                )}
                {enabled.customerService && (
                  <MetricCard
                    label="CS Open"
                    value={n(csMetrics?.queued)}
                    sub="Customer service tickets open"
                    tone={(csMetrics?.queued ?? 0) > 0 ? 'caution' : 'success'}
                  />
                )}
              </InlineGrid>
            </BlockStack>

            {/* ── Section 2: Queue Backlog ───────────────────────────────── */}
            <BlockStack gap="300">
              <SectionTitle title="Queue Backlog" sub="Current queue sizes — real-time" />
              <InlineGrid columns={3} gap="300">
                {enabled.confirmation && (
                  <MetricCard
                    label="CF Backlog"
                    value={n(cfMetrics?.queued)}
                    sub={totalOrders > 0 ? `${cfQueuePct} of total orders` : 'Awaiting confirmation'}
                    tone={(cfMetrics?.queued ?? 0) > 0 ? 'caution' : 'success'}
                  />
                )}
                {enabled.shippingConfirmation && (
                  <MetricCard
                    label="SC Backlog"
                    value={n(scMetrics?.queued)}
                    sub={totalOrders > 0 ? `${scQueuePct} of total orders` : 'Awaiting shipping'}
                    tone={(scMetrics?.queued ?? 0) > 0 ? 'caution' : 'success'}
                  />
                )}
                <MetricCard
                  label="Cancelled"
                  value={n(orderStats?.cancelled)}
                  sub={`${cancelledPct} of total orders`}
                  tone={(orderStats?.cancelled ?? 0) > 0 ? 'critical' : 'success'}
                />
              </InlineGrid>
            </BlockStack>

            {/* ── Section 3: FIFO ───────────────────────────────────────── */}
            {hasFifo && fifoData !== null && (
              <BlockStack gap="300">
                <SectionTitle
                  title="FIFO Inventory"
                  sub={`${fifoData.total} orders — stock allocation snapshot`}
                />
                <InlineGrid columns={3} gap="300">
                  <MetricCard
                    label="Fully Reserved"
                    value={n(fifoData.full)}
                    sub={`${pct(fifoData.full, fifoData.total)} — all items allocated`}
                    tone={fifoData.full > 0 ? 'success' : undefined}
                  />
                  <MetricCard
                    label="Partially Reserved"
                    value={n(fifoData.partial)}
                    sub={`${pct(fifoData.partial, fifoData.total)} — some items missing`}
                    tone={fifoData.partial > 0 ? 'caution' : undefined}
                  />
                  <MetricCard
                    label="No Stock"
                    value={n(fifoData.none)}
                    sub={`${pct(fifoData.none, fifoData.total)} — zero allocation`}
                    tone={fifoData.none > 0 ? 'critical' : undefined}
                  />
                </InlineGrid>
              </BlockStack>
            )}

            {/* ── Section 4: Activity ───────────────────────────────────── */}
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <SectionTitle title={`Activity — ${periodLabel}`} />
                <Select label="Period" labelHidden options={PERIODS} value={period} onChange={setPeriod} />
              </InlineStack>

              {periodLoading ? (
                <InlineStack gap="200" blockAlign="center">
                  <Spinner size="small" />
                  <Text as="p" variant="bodySm" tone="subdued">Loading {periodLabel} data...</Text>
                </InlineStack>
              ) : (
                <InlineGrid columns={{ xs: 2, sm: 4 }} gap="300">
                  <MetricCard
                    label="Confirmed by CF"
                    value={hasExec ? n(cfConfirmedPeriod) : null}
                    sub="Orders entered ops pipeline"
                  />
                  <MetricCard
                    label="Approved for Shipping"
                    value={hasExec ? n(scApprovedPeriod) : null}
                    sub="Waybills created (SC)"
                  />
                  <MetricCard
                    label="Scanned Out"
                    value={n(s?.activity.scannedOut)}
                    sub="Physically handed to courier"
                  />
                  <MetricCard
                    label="SC Throughput"
                    value={hasExec && throughputRate !== null ? `${throughputRate}%` : null}
                    sub="Approved for shipping / confirmed"
                    tone={
                      throughputRate == null ? undefined
                      : throughputRate >= 80  ? 'success'
                      : throughputRate >= 50  ? 'caution'
                      : 'critical'
                    }
                  />
                  {!hasExec && period === 'yesterday' && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Card>
                        <Text as="p" variant="bodySm" tone="subdued">
                          CF/SC metrics are not available for Yesterday. Scanned Out count is shown above.
                        </Text>
                      </Card>
                    </div>
                  )}
                </InlineGrid>
              )}
            </BlockStack>

            {/* ── Section 5: Warehouse Flow ─────────────────────────────── */}
            {s !== null && (
              <BlockStack gap="300">
                <SectionTitle
                  title="Warehouse Flow"
                  sub="What left the warehouse and what's coming back"
                />
                <InlineGrid columns={2} gap="300">
                  <MetricCard
                    label="With Bosta"
                    value={n(s.pipeline.pickedUp)}
                    sub="Left our warehouse — awaiting Bosta confirmation"
                    tone={s.pipeline.pickedUp > 0 ? 'caution' : undefined}
                  />
                  <MetricCard
                    label="Returned to Us"
                    value={n(s.pipeline.returned)}
                    sub="Failed deliveries — need to receive back"
                    tone={s.pipeline.returned > 0 ? 'critical' : undefined}
                  />
                </InlineGrid>
              </BlockStack>
            )}

            {/* ── Section 6: Timing ─────────────────────────────────────── */}
            {s !== null && (
              <BlockStack gap="300">
                <SectionTitle
                  title="Timing"
                  sub="Warehouse dispatch speed — computed from all available data"
                />
                <InlineGrid columns={2} gap="300">
                  <MetricCard
                    label="Waybill → Scan Out"
                    value={s.timing.avgScToScanOutHours !== null ? `${s.timing.avgScToScanOutHours}h avg` : null}
                    sub="Time from SC approval to physical handoff to courier"
                    tone={
                      s.timing.avgScToScanOutHours == null ? undefined
                      : s.timing.avgScToScanOutHours <= 4  ? 'success'
                      : s.timing.avgScToScanOutHours <= 24 ? 'caution'
                      : 'critical'
                    }
                  />
                  <MetricCard
                    label="Peak Scan Hour"
                    value={s.timing.peakScanHour !== null ? fmtHour(s.timing.peakScanHour) : null}
                    sub="Hour of day with most outbound scans"
                  />
                </InlineGrid>
              </BlockStack>
            )}

          </BlockStack>
        </div>
      </div>
    </Page>
  )
}
